import type {
  ApprovalSummary,
  ApprovalScope,
  ApprovalPage,
  ApprovalPageArg,
  ApprovalDecisionBody,
  ApprovalDecisionResult,
  OrgApprovalPageArg,
} from '@/types/play-approvals';
import type { HoldChoice, SampleEstimate } from '@/types/custom-playbooks';
import type { TryBudgetSetting } from '@/types/gtm-agents';
import type {
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { createApi, skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { toast } from 'sonner';
import { baseQueryWithAuth } from '../base-query';
import { browserTimezone } from '@/components/builder/timezone.util';
import { advancedPlaybooksApi } from './custom-playbooks.feature';
import { rememberAgentMarkColours } from '@/containers/agents/agent-mark-order.store';
import { duplicateSummaryOf } from '@/containers/agents/duplicate-review/duplicate-summary.util';
import { isBriefEntry, isBriefTrigger } from '@/containers/agents/brief.util';
import {
  isQueuedRun,
  triggerKind,
  type CustomGraph,
  type JobFilters,
  type JobAudienceSize,
  type PostFilters,
  type PostAudienceSize,
  type ToolAudienceSize,
  type ToolFilters,
  type CustomPlaybookDto,
  type CustomRunSummary,
  type PlayCrm,
  type QueuedRunDto,
  type TriggerConfig,
} from '@/types/custom-playbooks';
import type {
  AcceptPlayProposalResponse,
  AgentConnectionInput,
  AgentDto,
  AgentTemplateGraphDto,
  AgentTemplateKey,
  AgentsActivityDto,
  AttachPlayBody,
  CreateAgentBody,
  CrmScanStatusDto,
  CreateSkillBody,
  PatchAgentBody,
  PatchSkillBody,
  PlayProposalDto,
  CandidateTestDto,
  OwnerChangeDto,
  SetStatusBody,
  SkillDto,
  TriggerInput,
} from '@/types/gtm-agents';
import type {
  DuplicateDecideBody,
  DuplicateDecideResponse,
  DuplicateGroupDetail,
  DuplicateGroupFilters,
  DuplicateGroupsPage,
  DuplicateGroupsSummaryDto,
} from '@/types/duplicate-groups';

export interface SkillCatalogScope {
  organizationId: string;
  agentId?: string;
  proposalId?: string;
}

/** The CRMs the owned Agent's deal-stages route reads. */
export type DealStageCrm = 'salesforce' | 'hubspot' | 'attio';

/** Native stage identity, returned under the owned Agent. HubSpot rows carry
 *  their pipeline. Attio rows are a status id and title only: Attio has no
 *  pipelines and no closed flag on a stage. */
export interface DealStageValue {
  id: string;
  name: string;
  pipeline_id?: string;
  pipeline?: string;
  closed?: boolean;
}

/**
 * Agents API: wire contract for the backend routers at cleanse-api-v2
 * `cleanse_api/api/agents/gtm_agents.py` (/api/v1/gtm-agents) and
 * `skills.py` (/api/v1/skills), both behind `require_gtm_agents`. Callers
 * skip every query while `useAgentsAccess()` is locked, so nothing fires for
 * an organization without Agents.
 *
 * An agent owns its play, and several agent writes land on the play row too:
 * attaching and detaching, a trigger or results-list change, a status change,
 * a duplicate. Those refresh the playbook cache as well, or the embedded
 * builder would hydrate the play as it was and a later save would put it back.
 *
 * Writes to one agent go out one at a time, in the order they were made, and
 * each shows at once. The server stores a nested field (limits, delivery,
 * tools) whole, so each write carries the whole field, built from what the
 * page shows; a read landing between two writes would hand the second an old
 * copy and undo the first. So a write does not reread the agent: once no
 * other write to it is pending, the server's answer becomes the record.
 *
 * Every agent a read, a create or a duplicate returns hands its stored mark
 * colour to the marks (`rememberMarks`), so a mark draws it wherever the
 * agent was read, whether or not a list is loaded.
 */

type PlayTag = { type: 'CustomPlaybook'; id: string };

const writeQueues = new Map<string, Promise<unknown>>();
const pendingWrites = new Map<string, number>();

/** Runs `send` after every earlier write to the agent has settled. */
function inOrder<T>(agentId: string, send: () => Promise<T>): Promise<T> {
  const previous = writeQueues.get(agentId) ?? Promise.resolve();
  const run = previous.then(send);
  const tail = run.then(
    () => undefined,
    () => undefined
  );
  writeQueues.set(agentId, tail);
  void tail.then(() => {
    if (writeQueues.get(agentId) === tail) writeQueues.delete(agentId);
  });
  return run;
}

function beginWrite(agentId: string) {
  pendingWrites.set(agentId, (pendingWrites.get(agentId) ?? 0) + 1);
}

/** True when this was the last write pending for the agent. */
function endWrite(agentId: string): boolean {
  const left = (pendingWrites.get(agentId) ?? 1) - 1;
  if (left > 0) pendingWrites.set(agentId, left);
  else pendingWrites.delete(agentId);
  return left <= 0;
}

/**
 * A PATCH as the page should show it before the server answers. Skills wait
 * for the server, which resolves their names; triggers keep the fire times the
 * server computed.
 */
function applyPatch(draft: AgentDto, body: PatchAgentBody) {
  const {
    skillRefs,
    triggers,
    tools,
    limits,
    connections,
    playbookId,
    ...rest
  } = body;
  void skillRefs;
  Object.assign(draft, rest);
  if (rest.delivery)
    // The list the user writes is the list they picked (the server's
    // `configuredLeadListId`), not only where the engine stores records.
    draft.delivery = {
      ...rest.delivery,
      data: {
        ...rest.delivery.data,
        configuredLeadListId: rest.delivery.data.leadListId,
      },
    };
  if (tools) Object.assign(draft.tools, tools);
  if (limits) Object.assign(draft.limits, limits);
  if (connections) applyConnections(draft, connections);
  if (triggers) applyTriggerEntry(draft, triggers[0], playbookId);
}

/**
 * A PATCH of `connections` as the server stores it: the list is what the agent
 * stores, whole. What the server works out around each one (who it acts as,
 * whom the CRM shows) stays as it was until its answer arrives, and an app
 * the plays use that the write leaves out stays listed, as the server lists
 * it: on, as the agent owner, with nothing set.
 */
function applyConnections(
  draft: AgentDto,
  stored: readonly AgentConnectionInput[]
) {
  const before = draft.connections;
  const written = new Set(stored.map((input) => input.type));
  draft.connections = [
    ...stored.map((input) => ({
      ...before.find((item) => item.type === input.type),
      ...input,
      fromPlay: false,
    })),
    ...(draft.playConnectionTypes ?? [])
      .filter((type) => !written.has(type))
      .map((type) => ({
        ...before.find((item) => item.type === type),
        type,
        enabled: true,
        actingUserId: null,
        policy: {},
        fromPlay: true,
      })),
  ];
}

/**
 * A PATCH of `triggers` as the server writes it: its one entry goes to one
 * play and every other trigger stays. With `playbookId` it is that play's.
 * Without one, a brief's entry edits the agent's brief, or starts a brief
 * beside the plays in place of the agent's own trigger from before W2-24;
 * any other entry is its own play's. An empty list drops the agent's own
 * trigger.
 */
function applyTriggerEntry(
  draft: AgentDto,
  entry: TriggerInput | undefined,
  playbookId: string | undefined
) {
  const plays = draft.triggers.filter((trigger) => trigger.source === 'play');
  if (!entry) {
    draft.triggers = plays;
    return;
  }
  const brief = !playbookId && isBriefEntry(entry);
  const index = draft.triggers.findIndex((trigger) =>
    playbookId
      ? trigger.id === playbookId
      : brief
        ? isBriefTrigger(trigger)
        : trigger.id === entry.id
  );
  if (index >= 0) {
    const before = draft.triggers[index];
    draft.triggers[index] = {
      ...entry,
      id: before.id,
      lastFiredAt: before.lastFiredAt,
      nextAt: before.nextAt,
      health: before.health,
    };
  } else if (brief)
    draft.triggers = [...plays, { ...entry, lastFiredAt: null, nextAt: null }];
}

/** Hands the returned agents' stored mark colours to the marks. */
async function rememberMarks(
  queryFulfilled: PromiseLike<{ data: AgentDto | readonly AgentDto[] }>
) {
  try {
    const { data } = await queryFulfilled;
    rememberAgentMarkColours(Array.isArray(data) ? data : [data]);
  } catch {
    // A failed read returned no agent.
  }
}

/** The mailbox a play's Send an email steps now send from. */
export interface PlayMailboxDto {
  node_ids: string[];
  mailbox_id: string;
  mailbox_email: string;
}

function playTags(ids: readonly string[]): PlayTag[] {
  return [
    { type: 'CustomPlaybook', id: 'LIST' },
    ...ids.map((id) => ({ type: 'CustomPlaybook' as const, id })),
  ];
}

/** The duplicate groups' filters as query params; several statuses comma separated. */
export function duplicateGroupParams(
  filters: DuplicateGroupFilters
): Record<string, string> {
  const params: Record<string, string> = {};
  const { status, object, confidence, flags, run } = filters;
  if (status)
    params.status = typeof status === 'string' ? status : status.join(',');
  if (object) params.object = object;
  if (confidence) params.confidence = confidence;
  if (flags) params.flags = flags;
  if (run) params.run = run;
  return params;
}

/**
 * "3 merges reopened because the rules changed": what a cleanup play's save
 * or accepted proposal says when its new rules sent approved merges back to
 * review (W5 design 2.7). Null when it sent none.
 */
export function reopenedMergesNotice(count: unknown): string | null {
  const n =
    typeof count === 'number' && Number.isFinite(count) ? Math.floor(count) : 0;
  if (n <= 0) return null;
  return (
    n.toLocaleString() +
    (n === 1 ? ' merge' : ' merges') +
    ' reopened because the rules changed'
  );
}

/** The review screen's page size: the most the list route returns. */
export const DUPLICATE_GROUPS_PAGE_SIZE = 100;

export type ListDuplicateGroupsArg = DuplicateGroupFilters & {
  agentId: string;
};

type ProposalRead =
  | { data: PlayProposalDto; error?: undefined }
  | { error: FetchBaseQueryError; data?: undefined };
type SentRead = { data?: unknown; error?: FetchBaseQueryError };
type SendRead = (args: FetchArgs) => SentRead | PromiseLike<SentRead>;
type ProposalBatch = {
  send: SendRead;
  waiters: { proposalId: string; resolve: (read: ProposalRead) => void }[];
};

// The most ids the batch read takes (the API's cap).
const PROPOSAL_BATCH_MAX = 50;
const proposalBatches = new Map<string, ProposalBatch>();

/**
 * Reads a play proposal along with every other proposal of the same agent
 * asked for in the same tick, in one `GET /play-proposals?ids=` (Sentry
 * CLEANLIST-PORTAL-9M: a chat's N proposal cards made N GETs). Each card
 * keeps its own cache entry and `PlayProposal` tag, so an invalidation
 * refetches them together too. An API without the batch route (404) is read
 * one proposal at a time, as before.
 */
function readProposal(
  agentId: string,
  proposalId: string,
  organizationId: string | undefined,
  send: SendRead
): Promise<ProposalRead> {
  const key = `${agentId}\n${organizationId ?? ''}`;
  return new Promise((resolve) => {
    let batch = proposalBatches.get(key);
    if (!batch) {
      batch = { send, waiters: [] };
      proposalBatches.set(key, batch);
      setTimeout(() => {
        proposalBatches.delete(key);
        void flushProposals(agentId, organizationId, batch!);
      });
    }
    batch.waiters.push({ proposalId, resolve });
  });
}

async function flushProposals(
  agentId: string,
  organizationId: string | undefined,
  { send, waiters }: ProposalBatch
) {
  const headers = organizationId
    ? { headers: { 'X-Cleanlist-Expected-Org': organizationId } }
    : {};
  const url = `/gtm-agents/${agentId}/play-proposals`;
  const one = async (proposalId: string): Promise<ProposalRead> => {
    const read = await send({ url: `${url}/${proposalId}`, ...headers });
    return read.error
      ? { error: read.error }
      : { data: read.data as PlayProposalDto };
  };
  const ids = [...new Set(waiters.map((waiter) => waiter.proposalId))];
  const reads = new Map<string, ProposalRead>();
  for (let start = 0; start < ids.length; start += PROPOSAL_BATCH_MAX) {
    const chunk = ids.slice(start, start + PROPOSAL_BATCH_MAX);
    const batch =
      chunk.length > 1
        ? await send({ url, params: { ids: chunk.join(',') }, ...headers })
        : undefined;
    if (!batch || batch.error?.status === 404) {
      const singles = await Promise.all(chunk.map(one));
      chunk.forEach((id, index) => reads.set(id, singles[index]));
      continue;
    }
    const found = new Map(
      ((batch.data ?? []) as PlayProposalDto[]).map((row) => [row.id, row])
    );
    for (const id of chunk) {
      const row = found.get(id);
      reads.set(
        id,
        batch.error
          ? { error: batch.error }
          : row
            ? { data: row }
            : { error: { status: 404, data: { detail: 'Proposal not found' } } }
      );
    }
  }
  for (const { proposalId, resolve } of waiters)
    resolve(reads.get(proposalId)!);
}

export const gtmAgentsApi = createApi({
  reducerPath: 'gtmAgentsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'TryBudget',
    'GtmAgent',
    'Skill',
    'CustomRun',
    'PlayProposal',
    'CandidateTest',
    'CrmScan',
    'DuplicateGroup',
    'PlayApproval',
  ],
  endpoints: (builder) => ({
    getDealStages: builder.query<
      {
        crm: DealStageCrm;
        kind: 'stages';
        rows: DealStageValue[];
        more?: number;
        partial?: boolean;
      },
      {
        agentId: string;
        organizationId: string;
        crm: DealStageCrm;
        query?: string;
        workspaceId?: string;
      }
    >({
      query: ({ agentId, organizationId, crm, query = '' }) => ({
        url: `/gtm-agents/${agentId}/deal-stages`,
        params: { crm, query },
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      keepUnusedDataFor: 0,
    }),
    getPlayApprovalsSummary: builder.query<ApprovalSummary, ApprovalScope>({
      query: ({ agentId, organizationId }) => ({
        url: `/gtm-agents/${agentId}/approvals/summary`,
        method: 'GET',
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      providesTags: (_result, _error, scope) => [
        {
          type: 'PlayApproval',
          id: `${scope.organizationId}:${scope.agentId}`,
        },
      ],
    }),
    listPlayApprovals: builder.infiniteQuery<
      ApprovalPage,
      ApprovalPageArg,
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (last) => last.nextCursor ?? undefined,
      },
      query: ({
        queryArg: {
          agentId,
          organizationId,
          playbookId,
          nodeId,
          requestId,
          status,
        },
        pageParam,
      }) => ({
        url: `/gtm-agents/${agentId}/approvals`,
        method: 'GET',
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
        params: {
          playbookId,
          nodeId,
          requestId,
          status,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
      providesTags: (_result, _error, scope) => [
        {
          type: 'PlayApproval',
          id: `${scope.organizationId}:${scope.agentId}`,
        },
      ],
    }),
    listOrgApprovals: builder.infiniteQuery<
      ApprovalPage,
      OrgApprovalPageArg,
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (last) => last.nextCursor ?? undefined,
      },
      query: ({ queryArg: { organizationId, status }, pageParam }) => ({
        url: '/gtm-agents/approvals',
        method: 'GET',
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
        params: {
          mine: true,
          status,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
      providesTags: (_result, _error, scope) => [
        { type: 'PlayApproval', id: `${scope.organizationId}:inbox` },
      ],
    }),
    exportOrgApprovals: builder.mutation<string, OrgApprovalPageArg>({
      query: ({ organizationId, status }) => ({
        url: '/gtm-agents/approvals/export.csv',
        method: 'GET',
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
        params: { mine: true, status },
        responseHandler: 'text',
      }),
    }),
    decidePlayApprovals: builder.mutation<
      ApprovalDecisionResult,
      ApprovalScope & { body: ApprovalDecisionBody }
    >({
      query: ({ agentId, organizationId, body }) => ({
        url: `/gtm-agents/${agentId}/approvals/decide`,
        method: 'POST',
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
        body,
      }),
      invalidatesTags: (_result, _error, scope) => [
        {
          type: 'PlayApproval',
          id: `${scope.organizationId}:${scope.agentId}`,
        },
        { type: 'PlayApproval', id: `${scope.organizationId}:inbox` },
        'CustomRun',
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(advancedPlaybooksApi.util.invalidateTags(['CustomRun']));
        } catch {
          /* The scoped caller displays the server refusal. */
        }
      },
    }),
    exportPlayApprovals: builder.mutation<string, ApprovalPageArg>({
      query: ({
        agentId,
        organizationId,
        playbookId,
        nodeId,
        requestId,
        status,
      }) => ({
        url: `/gtm-agents/${agentId}/approvals/export.csv`,
        method: 'GET',
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
        params: { playbookId, nodeId, requestId, status },
        responseHandler: 'text',
      }),
    }),
    sizeToolAudience: builder.mutation<
      ToolAudienceSize,
      {
        orgId: string;
        toolFilters: ToolFilters;
        companyFilters: ToolAudienceSize['companyFilters'];
      }
    >({
      query: ({ orgId, toolFilters, companyFilters }) => ({
        url: '/gtm-agents/tool-audience/size',
        method: 'POST',
        headers: { 'X-Cleanlist-Expected-Org': orgId },
        body: { toolFilters, companyFilters },
      }),
    }),
    toolFilterValues: builder.query<
      { query: string; values: string[] },
      { orgId: string; query: string }
    >({
      query: ({ orgId, query }) => ({
        url: '/gtm-agents/tool-audience/values',
        headers: { 'X-Cleanlist-Expected-Org': orgId },
        params: { query },
      }),
    }),
    sizePostAudience: builder.mutation<
      PostAudienceSize,
      {
        orgId: string;
        postFilters: PostFilters;
        peopleFilters: PostAudienceSize['peopleFilters'];
      }
    >({
      query: ({ postFilters, peopleFilters }) => ({
        url: '/gtm-agents/post-audience/size',
        method: 'POST',
        body: { postFilters, peopleFilters },
      }),
    }),
    sizeJobAudience: builder.mutation<
      JobAudienceSize,
      { orgId: string; jobFilters: JobFilters }
    >({
      query: ({ jobFilters }) => ({
        url: '/gtm-agents/job-audience/size',
        method: 'POST',
        body: { jobFilters },
      }),
    }),
    jobFilterValues: builder.query<
      { field: string; query: string; values: string[] },
      { orgId: string; field: 'job_titles' | 'technologies'; query: string }
    >({
      query: ({ field, query }) => ({
        url: '/gtm-agents/job-audience/values',
        params: { field, query },
      }),
    }),
    listAgents: builder.query<AgentDto[], void | { organizationId: string }>({
      query: (scope) => ({
        url: '/gtm-agents',
        method: 'GET',
        ...(scope
          ? { headers: { 'X-Cleanlist-Expected-Org': scope.organizationId } }
          : {}),
      }),
      onQueryStarted: (_arg, { queryFulfilled }) =>
        rememberMarks(queryFulfilled),
      providesTags: (result) => [
        { type: 'GtmAgent' as const, id: 'LIST' },
        ...(result ?? []).map((agent) => ({
          type: 'GtmAgent' as const,
          id: agent.id,
        })),
      ],
    }),
    getAgent: builder.query<AgentDto, string>({
      query: (id) => ({ url: `/gtm-agents/${id}`, method: 'GET' }),
      onQueryStarted: (_id, { queryFulfilled }) =>
        rememberMarks(queryFulfilled),
      providesTags: (_result, _error, id) => [{ type: 'GtmAgent', id }],
    }),
    // A template's graph, built on `crm` (`none`: without CRM steps), or when
    // absent on the CRM the server resolves from the org's plan and
    // connections, with its schedule in the member's browser time zone. Every
    // read has fresh ids, so nothing is kept. An org whose plan doesn't
    // include it gets a 403.
    getAgentTemplateGraph: builder.query<
      AgentTemplateGraphDto,
      {
        key: AgentTemplateKey;
        crm?: PlayCrm | 'none';
        organizationId: string;
      }
    >({
      query: ({ key, crm, organizationId }) => ({
        url: `/gtm-agents/templates/${key}/graph`,
        params: { crm, timezone: browserTimezone() },
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      keepUnusedDataFor: 0,
    }),
    createAgent: builder.mutation<
      AgentDto,
      CreateAgentBody & { organizationId?: string }
    >({
      query: ({ organizationId, ...body }) => ({
        url: '/gtm-agents',
        method: 'POST',
        body,
        ...(organizationId
          ? { headers: { 'X-Cleanlist-Expected-Org': organizationId } }
          : {}),
      }),
      invalidatesTags: [{ type: 'GtmAgent', id: 'LIST' }],
      // Its first screen opens before the list is read again.
      onQueryStarted: (_body, { queryFulfilled }) =>
        rememberMarks(queryFulfilled),
    }),
    patchAgent: builder.mutation<
      AgentDto,
      { id: string; body: PatchAgentBody; organizationId?: string }
    >({
      queryFn: ({ id, body, organizationId }, _api, _extraOptions, baseQuery) =>
        inOrder(id, async () => {
          const result = await baseQuery({
            url: `/gtm-agents/${id}`,
            method: 'PATCH',
            body,
            ...(organizationId
              ? { headers: { 'X-Cleanlist-Expected-Org': organizationId } }
              : {}),
          });
          return result.error
            ? { error: result.error }
            : { data: result.data as AgentDto };
        }),
      // A cleanup play's approved merges go back to review when its CRM
      // writes stop: the connection removed or set to Never, or Write records
      // turned off (W5 design 4.7).
      invalidatesTags: (_result, _error, { body }) => [
        { type: 'GtmAgent', id: 'LIST' },
        ...('connections' in body || 'tools' in body
          ? (['DuplicateGroup'] as const)
          : []),
      ],
      // A trigger is the play's trigger card and the results list is the
      // play's list: both are written onto the play too.
      async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
        beginWrite(id);
        const optimistic = dispatch(
          gtmAgentsApi.util.updateQueryData('getAgent', id, (draft) => {
            applyPatch(draft, body);
          })
        );
        // A new name shows at once wherever the list does (the sidebar, the
        // Agents page, the switcher) too.
        const { name } = body;
        const listed =
          name === undefined
            ? undefined
            : dispatch(
                gtmAgentsApi.util.updateQueryData(
                  'listAgents',
                  undefined,
                  (draft) => {
                    const item = draft.find((agent) => agent.id === id);
                    if (item) item.name = name;
                  }
                )
              );
        let saved: AgentDto | undefined;
        try {
          saved = (await queryFulfilled).data;
        } catch {
          // The caller reads the failure off its own unwrap().
          optimistic.undo();
          listed?.undo();
        }
        settleWrite(
          id,
          saved,
          dispatch,
          'delivery' in body || 'connections' in body || 'tools' in body
        );
        if (saved && ('triggers' in body || 'delivery' in body))
          dispatch(
            advancedPlaybooksApi.util.invalidateTags(playTags(saved.playIds))
          );
        // The canvas asks the validate route about the agent's settings as
        // soon as the cache shows them, which can be before this write lands;
        // an answer read off the old settings would stay under the new ones.
        if (saved && ('tools' in body || 'connections' in body))
          dispatch(advancedPlaybooksApi.util.invalidateTags(['AgentSettings']));
      },
    }),
    setAgentStatus: builder.mutation<
      AgentDto,
      { id: string; body: SetStatusBody; organizationId?: string }
    >({
      queryFn: ({ id, body, organizationId }, _api, _extraOptions, baseQuery) =>
        inOrder(id, async () => {
          const result = await baseQuery({
            url: `/gtm-agents/${id}/status`,
            method: 'POST',
            body,
            ...(organizationId
              ? { headers: { 'X-Cleanlist-Expected-Org': organizationId } }
              : {}),
          });
          return result.error
            ? { error: result.error }
            : { data: result.data as AgentDto };
        }),
      // Turning a cleanup play's agent on can put its CRM scan in demand, and
      // turning it off sends its merges back to review (W5 design 4.7).
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'GtmAgent', id: 'LIST' },
        { type: 'CrmScan', id },
        'DuplicateGroup',
      ],
      // Turning on or pausing switches the play and recomputes its next run.
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        beginWrite(id);
        let saved: AgentDto | undefined;
        try {
          saved = (await queryFulfilled).data;
        } catch {
          // The caller reads the failure off its own unwrap().
        }
        settleWrite(id, saved, dispatch);
        if (saved)
          dispatch(
            advancedPlaybooksApi.util.invalidateTags(playTags(saved.playIds))
          );
      },
    }),
    // Hands the agent and its plays to another member. The agent's new
    // owner shows at once; its plays' runs act as them from the next one.
    changeAgentOwner: builder.mutation<
      OwnerChangeDto,
      { id: string; userId: string }
    >({
      queryFn: ({ id, userId }, _api, _extraOptions, baseQuery) =>
        inOrder(id, async () => {
          const result = await baseQuery({
            url: `/gtm-agents/${id}/owner`,
            method: 'POST',
            body: { userId },
          });
          return result.error
            ? { error: result.error }
            : { data: result.data as OwnerChangeDto };
        }),
      invalidatesTags: [{ type: 'GtmAgent', id: 'LIST' }],
      async onQueryStarted({ id, userId }, { dispatch, queryFulfilled }) {
        beginWrite(id);
        // Shown at once, as the server stores it: a connection that acted as
        // the old owner follows the new one. A write made meanwhile is built
        // from this, so it can't pin the old owner back.
        const optimistic = dispatch(
          gtmAgentsApi.util.updateQueryData('getAgent', id, (draft) => {
            const previous = draft.ownerUserId;
            draft.ownerUserId = userId;
            draft.ownerChangePending = true;
            for (const connection of draft.connections)
              if (connection.actingUserId === previous) {
                connection.actingUserId = null;
                connection.actsAsOwner = true;
              }
          })
        );
        let saved: AgentDto | undefined;
        try {
          saved = (await queryFulfilled).data.agent;
        } catch {
          // The caller reads the failure off its own unwrap().
          optimistic.undo();
        }
        settleWrite(id, saved, dispatch);
        if (saved)
          // The plays run as the new owner, and the canvas's checks of the
          // agent's settings name who each connection acts as.
          dispatch(
            advancedPlaybooksApi.util.invalidateTags([
              ...playTags(saved.playIds),
              'AgentSettings',
            ])
          );
      },
    }),
    attachPlay: builder.mutation<
      AgentDto,
      { id: string; body?: AttachPlayBody; organizationId?: string }
    >({
      queryFn: ({ id, body, organizationId }, _api, _extraOptions, baseQuery) =>
        inOrder(id, async () => {
          const result = await baseQuery({
            url: `/gtm-agents/${id}/plays`,
            method: 'POST',
            body: body ?? {},
            ...(organizationId
              ? { headers: { 'X-Cleanlist-Expected-Org': organizationId } }
              : {}),
          });
          return result.error
            ? { error: result.error }
            : { data: result.data as AgentDto };
        }),
      invalidatesTags: [{ type: 'GtmAgent', id: 'LIST' }],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        beginWrite(id);
        let saved: AgentDto | undefined;
        try {
          saved = (await queryFulfilled).data;
        } catch {
          // The caller reads the failure off its own unwrap().
        }
        settleWrite(id, saved, dispatch);
        if (saved)
          dispatch(
            advancedPlaybooksApi.util.invalidateTags(playTags(saved.playIds))
          );
      },
    }),
    duplicateAgent: builder.mutation<AgentDto, string>({
      query: (id) => ({ url: `/gtm-agents/${id}/duplicate`, method: 'POST' }),
      invalidatesTags: [{ type: 'GtmAgent', id: 'LIST' }],
      // The copy carries a copy of the play, and a mark colour of its own.
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          rememberAgentMarkColours([data]);
          dispatch(
            advancedPlaybooksApi.util.invalidateTags(playTags(data.playIds))
          );
        } catch {
          // The caller reads the failure off its own unwrap().
        }
      },
    }),
    deleteAgent: builder.mutation<void, string>({
      query: (id) => ({ url: `/gtm-agents/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'GtmAgent', id },
        { type: 'GtmAgent', id: 'LIST' },
      ],
    }),
    getTryBudget: builder.query<TryBudgetSetting, { organizationId: string }>({
      query: ({ organizationId }) => ({
        url: '/gtm-agents/try-budget',
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      providesTags: [{ type: 'TryBudget', id: 'ORG' }],
    }),
    setTryBudget: builder.mutation<
      TryBudgetSetting,
      { organizationId: string; budgetCredits: number }
    >({
      query: ({ organizationId, budgetCredits }) => ({
        url: '/gtm-agents/try-budget',
        method: 'PUT',
        body: { budgetCredits },
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      invalidatesTags: [{ type: 'TryBudget', id: 'ORG' }],
    }),
    getAgentSampleEstimate: builder.query<
      SampleEstimate,
      {
        id: string;
        playbookId: string;
        organizationId: string;
        /** Off-flag Sample 5 records, or 1 from a picked record; Try it sends none. */
        records?: number;
      }
    >({
      query: ({ id, playbookId, organizationId, records = 5 }) => ({
        url: `/gtm-agents/${id}/sample-estimate`,
        params: { records, playbookId },
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
    }),
    runAgentSample: builder.mutation<
      CustomRunSummary,
      {
        id: string;
        playbookId: string;
        organizationId: string;
        /** Off-flag Sample 5 records, or 1 from a picked record; Try it sends none. */
        records?: number;
        confirmCredits: number;
        /** Try it (try_it_v1): the reviewed quote. */
        quoteId?: string;
        /** W2-56, off-flag only: the one picked record this sample starts from. */
        testRecord?: { id: string };
      }
    >({
      query: ({
        id,
        playbookId,
        organizationId,
        records,
        confirmCredits,
        quoteId,
        testRecord,
      }) => ({
        url: `/gtm-agents/${id}/run-sample`,
        method: 'POST',
        body: {
          playbookId,
          ...(records !== undefined ? { records } : {}),
          confirmCredits,
          ...(quoteId ? { quoteId } : {}),
          ...(testRecord ? { testRecord } : {}),
        },
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      invalidatesTags: [{ type: 'CustomRun', id: 'LIST' }],
    }),
    // Starts a dry run of the agent's play, the same run the builder's Run
    // Test starts. Refused while the agent has no play.
    runAgentTest: builder.mutation<
      CustomRunSummary | QueuedRunDto,
      | string
      | {
          id: string;
          playbookId: string;
          organizationId: string;
          /** W2-56: the one picked record the test starts from. */
          testRecord?: { id: string };
        }
    >({
      query: (scope) => ({
        url: `/gtm-agents/${typeof scope === 'string' ? scope : scope.id}/run-test`,
        method: 'POST',
        ...(typeof scope === 'string'
          ? {}
          : {
              body: {
                playbookId: scope.playbookId,
                ...(scope.testRecord ? { testRecord: scope.testRecord } : {}),
              },
              headers: { 'X-Cleanlist-Expected-Org': scope.organizationId },
            }),
      }),
      // A cleanup play's test may queue behind a scan it asks for.
      invalidatesTags: (_result, _error, scope) => [
        { type: 'CustomRun', id: 'LIST' },
        { type: 'CrmScan', id: typeof scope === 'string' ? scope : scope.id },
      ],
    }),
    // The CRM scan behind the agent's cleanup play (W5 design 3.2); idle for
    // any other play. Any member of the org may read it.
    getAgentCrmScan: builder.query<
      CrmScanStatusDto,
      string | { id: string; playbookId: string; organizationId: string }
    >({
      query: (scope) => ({
        url: `/gtm-agents/${typeof scope === 'string' ? scope : scope.id}/crm-scan`,
        method: 'GET',
        ...(typeof scope === 'string'
          ? {}
          : {
              params: { playbookId: scope.playbookId },
              headers: { 'X-Cleanlist-Expected-Org': scope.organizationId },
            }),
      }),
      providesTags: (_result, _error, scope) => [
        { type: 'CrmScan', id: typeof scope === 'string' ? scope : scope.id },
      ],
    }),
    // The duplicate groups a cleanup play found (W5 design 7), a page at a
    // time. Any member of the org may read them.
    listDuplicateGroups: builder.infiniteQuery<
      DuplicateGroupsPage,
      ListDuplicateGroupsArg,
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      },
      query: ({ queryArg: { agentId, ...filters }, pageParam }) => ({
        url: `/gtm-agents/${agentId}/duplicate-groups`,
        method: 'GET',
        params: {
          ...duplicateGroupParams(filters),
          limit: DUPLICATE_GROUPS_PAGE_SIZE,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
      providesTags: (result) => [
        { type: 'DuplicateGroup' as const, id: 'LIST' },
        ...(result?.pages ?? []).flatMap((page) =>
          page.groups.map((group) => ({
            type: 'DuplicateGroup' as const,
            id: group.id,
          }))
        ),
      ],
    }),
    // The agent's groups by where they stand, and whether the viewer may
    // merge them (an org admin or the person who connected the CRM). Read
    // through the contract's reader: a field the server leaves out reads as
    // zero or no, never as a crash.
    getDuplicateGroupsSummary: builder.query<DuplicateGroupsSummaryDto, string>(
      {
        query: (agentId) => ({
          url: `/gtm-agents/${agentId}/duplicate-groups/summary`,
          method: 'GET',
        }),
        transformResponse: (raw: unknown) => duplicateSummaryOf(raw),
        providesTags: [{ type: 'DuplicateGroup', id: 'SUMMARY' }],
      }
    ),
    getDuplicateGroup: builder.query<
      DuplicateGroupDetail,
      { agentId: string; groupId: string }
    >({
      query: ({ agentId, groupId }) => ({
        url: `/gtm-agents/${agentId}/duplicate-groups/${groupId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { groupId }) => [
        { type: 'DuplicateGroup', id: groupId },
      ],
    }),
    // Picks the record a group keeps, while it is open or approved; an
    // approved group goes back to open, so the counts move too.
    patchDuplicateGroup: builder.mutation<
      DuplicateGroupDetail,
      { agentId: string; groupId: string; masterId: string }
    >({
      query: ({ agentId, groupId, masterId }) => ({
        url: `/gtm-agents/${agentId}/duplicate-groups/${groupId}`,
        method: 'PATCH',
        body: { masterId },
      }),
      invalidatesTags: (_result, _error, { groupId }) => [
        { type: 'DuplicateGroup', id: groupId },
        { type: 'DuplicateGroup', id: 'LIST' },
        { type: 'DuplicateGroup', id: 'SUMMARY' },
      ],
    }),
    // Merge (which starts a merge run), not duplicates, skip, reopen or
    // refresh, for the groups named or the ones a filter covers. Every group
    // and every run's counts can move, and a merge adds a run. A run's
    // detail counts its groups too (the Play tab's "9 groups waiting for
    // review"), so every run reads again, not only the lists.
    decideDuplicateGroups: builder.mutation<
      DuplicateDecideResponse,
      { agentId: string; body: DuplicateDecideBody }
    >({
      query: ({ agentId, body }) => ({
        url: `/gtm-agents/${agentId}/duplicate-groups/decide`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DuplicateGroup', 'CustomRun'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // The caller reads the failure off its own unwrap().
          return;
        }
        // The builder's run list and each run's detail live in the
        // playbooks slice.
        dispatch(advancedPlaybooksApi.util.invalidateTags(['CustomRun']));
      },
    }),
    // The groups a filter covers as CSV, read with the viewer's token and
    // saved by the caller. The server sanitizes every cell.
    exportDuplicateGroups: builder.mutation<string, ListDuplicateGroupsArg>({
      query: ({ agentId, ...filters }) => ({
        url: `/gtm-agents/${agentId}/duplicate-groups/export.csv`,
        method: 'GET',
        params: duplicateGroupParams(filters),
        responseHandler: 'text',
      }),
    }),
    listAgentRuns: builder.query<CustomRunSummary[], string>({
      query: (id) => ({ url: `/gtm-agents/${id}/runs`, method: 'GET' }),
      providesTags: [{ type: 'CustomRun', id: 'LIST' }],
    }),
    // The libraries' activity: runs per day by play and by agent. A server
    // without the route answers with an error, which the libraries read as
    // no activity to show.
    getAgentsActivity: builder.query<AgentsActivityDto, number>({
      query: (days) => ({
        url: '/gtm-agents/activity',
        method: 'GET',
        params: { days },
      }),
      providesTags: [{ type: 'CustomRun', id: 'LIST' }],
    }),
    // A play proposal Clu made; the Play tab shows it on the canvas for review.
    getPlayProposal: builder.query<
      PlayProposalDto,
      { agentId: string; proposalId: string; organizationId?: string }
    >({
      queryFn: ({ agentId, proposalId, organizationId }, _api, _extra, send) =>
        readProposal(agentId, proposalId, organizationId, send),
      providesTags: (_result, _error, { proposalId }) => [
        { type: 'PlayProposal', id: proposalId },
      ],
    }),
    startCandidateTest: builder.mutation<
      CandidateTestDto,
      { agentId: string; proposalId: string; organizationId: string }
    >({
      query: ({ agentId, proposalId, organizationId }) => ({
        url: `/gtm-agents/${agentId}/play-proposals/${proposalId}/tests`,
        method: 'POST',
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      invalidatesTags: (_result, _error, { proposalId }) => [
        { type: 'CandidateTest', id: proposalId },
      ],
    }),
    getCandidateTest: builder.query<
      CandidateTestDto,
      {
        agentId: string;
        proposalId: string;
        organizationId: string;
        runId: string;
      }
    >({
      query: ({ agentId, proposalId, organizationId, runId }) => ({
        url: `/gtm-agents/${agentId}/play-proposals/${proposalId}/tests/${runId}`,
        method: 'GET',
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      providesTags: (_result, _error, { proposalId, agentId }) => [
        { type: 'CandidateTest', id: proposalId },
        { type: 'GtmAgent', id: agentId },
        { type: 'PlayProposal', id: proposalId },
      ],
    }),
    // Saves the proposal as the agent's play. A 409 means the play or the
    // proposal changed after it was made.
    acceptPlayProposal: builder.mutation<
      AcceptPlayProposalResponse,
      {
        agentId: string;
        proposalId: string;
        holds?: HoldChoice;
        /** The review card's digest, which confirms its steps. */
        consent?: string;
        organizationId?: string;
      }
    >({
      queryFn: (
        { agentId, proposalId, holds, consent, organizationId },
        _api,
        _extraOptions,
        baseQuery
      ) =>
        inOrder(agentId, async () => {
          const result = await baseQuery({
            url: `/gtm-agents/${agentId}/play-proposals/${proposalId}/accept`,
            method: 'POST',
            ...(organizationId
              ? { headers: { 'X-Cleanlist-Expected-Org': organizationId } }
              : {}),
            ...(holds || consent
              ? {
                  body: {
                    ...(holds ? { holds } : {}),
                    ...(consent ? { consent } : {}),
                  },
                }
              : {}),
          });
          return result.error
            ? { error: result.error }
            : { data: result.data as AcceptPlayProposalResponse };
        }),
      // Accepting a cleanup proposal asks for the CRM scan, and new rules
      // re-evaluate the play's groups (W5 design 2.7).
      invalidatesTags: (_result, _error, { agentId, proposalId }) => [
        { type: 'GtmAgent', id: 'LIST' },
        { type: 'PlayProposal', id: proposalId },
        'Skill',
        { type: 'CrmScan', id: agentId },
        'DuplicateGroup',
      ],
      async onQueryStarted({ agentId }, { dispatch, queryFulfilled }) {
        beginWrite(agentId);
        let saved: AgentDto | undefined;
        let reopened: number | undefined;
        try {
          // The count is the save's news, not part of the agent record.
          const { reopenedMerges, ...agent } = (await queryFulfilled).data;
          saved = agent;
          reopened = reopenedMerges;
        } catch {
          // The caller reads the failure off its own unwrap().
        }
        settleWrite(agentId, saved, dispatch);
        if (saved)
          dispatch(
            advancedPlaybooksApi.util.invalidateTags(playTags(saved.playIds))
          );
        const notice = reopenedMergesNotice(reopened);
        if (notice) toast(notice);
      },
    }),
    // Drops the proposal; the play stays as it is. The server answers 204, so
    // the agent (its pendingProposalId) is read again once no write is pending.
    discardPlayProposal: builder.mutation<
      void,
      { agentId: string; proposalId: string; organizationId?: string }
    >({
      queryFn: (
        { agentId, proposalId, organizationId },
        _api,
        _extraOptions,
        baseQuery
      ) =>
        inOrder(agentId, async () => {
          const result = await baseQuery({
            url: `/gtm-agents/${agentId}/play-proposals/${proposalId}/discard`,
            method: 'POST',
            ...(organizationId
              ? { headers: { 'X-Cleanlist-Expected-Org': organizationId } }
              : {}),
          });
          return result.error ? { error: result.error } : { data: undefined };
        }),
      invalidatesTags: (_result, _error, { proposalId }) => [
        { type: 'PlayProposal', id: proposalId },
        'Skill',
      ],
      async onQueryStarted({ agentId }, { dispatch, queryFulfilled }) {
        beginWrite(agentId);
        try {
          await queryFulfilled;
        } catch {
          // The caller reads the failure off its own unwrap().
        }
        settleWrite(agentId, undefined, dispatch);
      },
    }),
    // Sets the mailbox a play's Send an email steps send from: the member's
    // own mailbox, picked on Clu's connection card after a Try or dry run
    // stopped on them. The server also sets each step's provider to the
    // mailbox's. It refuses another member's mailbox or step (403), a
    // missing agent, play or step (404), and a step that isn't an email
    // step or a mailbox that can't send (409); the card shows its message.
    setPlayMailbox: builder.mutation<
      PlayMailboxDto,
      {
        agentId: string;
        playbookId: string;
        nodeIds: readonly string[];
        mailboxId: string;
      }
    >({
      queryFn: (
        { agentId, playbookId, nodeIds, mailboxId },
        _api,
        _extraOptions,
        baseQuery
      ) =>
        inOrder(agentId, async () => {
          const result = await baseQuery({
            url: `/gtm-agents/${agentId}/play-mailbox`,
            method: 'POST',
            body: {
              playbook_id: playbookId,
              node_ids: nodeIds,
              mailbox_id: mailboxId,
            },
          });
          return result.error
            ? { error: result.error }
            : { data: result.data as PlayMailboxDto };
        }),
      invalidatesTags: (_result, error, { agentId }) =>
        error ? [] : [{ type: 'GtmAgent', id: agentId }],
      async onQueryStarted({ playbookId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            advancedPlaybooksApi.util.invalidateTags(playTags([playbookId]))
          );
        } catch {
          // The caller reads the failure off its own unwrap().
        }
      },
    }),
    listSkills: builder.query<SkillDto[], SkillCatalogScope | void>({
      query: (scope) => ({
        url: '/skills',
        method: 'GET',
        ...(scope
          ? {
              params: { agentId: scope.agentId, proposalId: scope.proposalId },
              headers: { 'X-Cleanlist-Expected-Org': scope.organizationId },
            }
          : {}),
      }),
      providesTags: (result) => [
        { type: 'Skill' as const, id: 'LIST' },
        ...(result ?? []).map((skill) => ({
          type: 'Skill' as const,
          id: skill.id,
        })),
      ],
    }),
    getSkill: builder.query<
      SkillDto,
      string | (SkillCatalogScope & { id: string })
    >({
      query: (scope) => ({
        url: `/skills/${encodeURIComponent(typeof scope === 'string' ? scope : scope.id)}`,
        method: 'GET',
        ...(typeof scope !== 'string'
          ? {
              params: { agentId: scope.agentId, proposalId: scope.proposalId },
              headers: { 'X-Cleanlist-Expected-Org': scope.organizationId },
            }
          : {}),
      }),
      providesTags: (_result, _error, scope) => [
        { type: 'Skill', id: typeof scope === 'string' ? scope : scope.id },
      ],
    }),
    // A skill's name shows on every agent that uses it, and a delete strips
    // it from them, so these refresh every agent, not only the list.
    createSkill: builder.mutation<SkillDto, CreateSkillBody>({
      query: (body) => ({ url: '/skills', method: 'POST', body }),
      invalidatesTags: [{ type: 'Skill', id: 'LIST' }, 'GtmAgent'],
    }),
    patchSkill: builder.mutation<
      SkillDto,
      { id: string; body: PatchSkillBody }
    >({
      query: ({ id, body }) => ({
        url: `/skills/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Skill', id },
        { type: 'Skill', id: 'LIST' },
        'GtmAgent',
      ],
    }),
    deleteSkill: builder.mutation<void, string>({
      query: (id) => ({
        url: `/skills/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Skill', id },
        { type: 'Skill', id: 'LIST' },
        'GtmAgent',
      ],
    }),
  }),
});

/** Whether a saved play is a cleanup play, by the server's word or its graph. */
function isCleanupSave(
  saved: CustomPlaybookDto,
  graph: CustomGraph | undefined
): boolean {
  return (
    saved.triggerType === 'crm_cleanup' ||
    (graph?.nodes ?? []).some(
      (node) =>
        node.type === 'trigger' &&
        triggerKind(node.config as Partial<TriggerConfig>) === 'crm_duplicates'
    )
  );
}

/**
 * A play save can turn a cleanup play on or change what it checks, and either
 * can put its CRM scan in demand (W5 design 3.2), so the trigger's scan line
 * reads again. The save names the play and not its agent, so every scan read
 * refreshes; only an open cleanup trigger holds one. It is wired from here
 * because this slice already reads the playbooks slice, and the reverse
 * import would be a cycle.
 */
advancedPlaybooksApi.enhanceEndpoints({
  endpoints: {
    // Run now on a cleanup play without a recent scan queues behind the scan
    // it starts (W5 3.3): the scan line reads again, and shows the run waiting.
    runCustomPlaybookNow: {
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (isQueuedRun(data))
            dispatch(gtmAgentsApi.util.invalidateTags(['CrmScan']));
        } catch {
          // The caller reads the failure off its own unwrap().
        }
      },
    },
    saveCustomPlaybook: {
      async onQueryStarted(
        { id, body },
        { dispatch, getState, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          // What blocks an agent (Blocked, Turn on, a test) is worked out from
          // its plays' graphs, so a saved play changes it: the agents it
          // belongs to read again.
          // The app's store holds both APIs: this one's cache says whose.
          const state = getState() as unknown as {
            [gtmAgentsApi.reducerPath]: ReturnType<typeof gtmAgentsApi.reducer>;
          };
          const owners = gtmAgentsApi.util
            .selectCachedArgsForQuery(state, 'getAgent')
            .filter((agentId) =>
              gtmAgentsApi.endpoints.getAgent
                .select(agentId)(state)
                .data?.playIds.includes(id)
            );
          if (owners.length)
            dispatch(
              gtmAgentsApi.util.invalidateTags(
                owners.map((agentId) => ({
                  type: 'GtmAgent' as const,
                  id: agentId,
                }))
              )
            );
          // New rules re-evaluate the play's groups, and a play turned off
          // sends its merges back to review (W5 design 2.7, 4.7). The save
          // says how many approved merges its rules sent back; every save
          // path lands here, so the builder and Clu both say it.
          if (isCleanupSave(data, body.graph)) {
            dispatch(
              gtmAgentsApi.util.invalidateTags(['CrmScan', 'DuplicateGroup'])
            );
            const notice = reopenedMergesNotice(data.reopenedMerges);
            if (notice) toast(notice);
          }
        } catch {
          // The caller reads the failure off its own unwrap().
        }
      },
    },
  },
});

/**
 * The saved answer, with the agent's gaps, quick replies and what it was
 * built from carried over from the cache when the answer has none of them
 * (CLE-478): a write that doesn't compute them must not clear the "CRM sync
 * off" state until the next read.
 */
function keepCompleteness(saved: AgentDto, current: AgentDto): AgentDto {
  const kept: Partial<AgentDto> = {};
  if (saved.gaps === undefined && current.gaps !== undefined)
    kept.gaps = current.gaps;
  if (saved.quickReplies === undefined && current.quickReplies !== undefined)
    kept.quickReplies = current.quickReplies;
  if (saved.builtFrom === undefined && current.builtFrom !== undefined)
    kept.builtFrom = current.builtFrom;
  return Object.keys(kept).length ? { ...saved, ...kept } : saved;
}

/**
 * Once the last pending write to an agent settles, the server's answer is the
 * record; when that last write was refused, read the agent again instead.
 */
function settleWrite(
  agentId: string,
  saved: AgentDto | undefined,
  dispatch: (action: unknown) => unknown,
  changesGaps = false
) {
  if (!endWrite(agentId)) return;
  if (saved) {
    dispatch(
      gtmAgentsApi.util.updateQueryData('getAgent', agentId, (current) =>
        keepCompleteness(saved, current)
      )
    );
    // A write that can change the gaps (delivery, connections, tools) but
    // answered without them would keep a stale "CRM sync off": read the agent
    // once no write is pending.
    if (changesGaps && saved.gaps === undefined)
      dispatch(
        gtmAgentsApi.util.invalidateTags([{ type: 'GtmAgent', id: agentId }])
      );
  } else
    dispatch(
      gtmAgentsApi.util.invalidateTags([{ type: 'GtmAgent', id: agentId }])
    );
}

type CacheState = Parameters<
  ReturnType<typeof gtmAgentsApi.endpoints.getAgent.select>
>[0];

/**
 * The agent as the cache holds it at this moment, with every write made so
 * far. React hears of a cache change up to a frame later, so a write built
 * from the last render can miss the one made just before it; build writes to
 * a whole field (limits, delivery, tools) from this instead.
 */
export function useAgentSnapshot(agentId: string): () => AgentDto | undefined {
  const store = useStore();
  return useCallback(
    () =>
      gtmAgentsApi.endpoints.getAgent.select(agentId)(
        store.getState() as CacheState
      ).data,
    [store, agentId]
  );
}

/**
 * The agent as the agents list last read it, while the list is in the cache;
 * nothing is fetched. What the workspace knows of an agent before the agent
 * itself loads.
 */
export function useListedAgent(agentId: string): AgentDto | undefined {
  return gtmAgentsApi.endpoints.listAgents.useQueryState(undefined, {
    selectFromResult: ({ data }) => ({
      listed: data?.find((agent) => agent.id === agentId),
    }),
  }).listed;
}

/** The agent while the cache holds it; nothing is fetched. */
export function useCachedAgent(
  agentId: string | undefined
): AgentDto | undefined {
  return gtmAgentsApi.endpoints.getAgent.useQueryState(agentId ?? skipToken)
    .currentData;
}

export const {
  useGetDealStagesQuery,
  useGetTryBudgetQuery,
  useSetTryBudgetMutation,
  useLazyGetAgentSampleEstimateQuery,
  useRunAgentSampleMutation,
  useGetPlayApprovalsSummaryQuery,
  useListPlayApprovalsInfiniteQuery,
  useListOrgApprovalsInfiniteQuery,
  useExportOrgApprovalsMutation,
  useDecidePlayApprovalsMutation,
  useExportPlayApprovalsMutation,
  useSizeJobAudienceMutation,
  useSizePostAudienceMutation,
  useSizeToolAudienceMutation,
  useToolFilterValuesQuery,
  useJobFilterValuesQuery,
  useListAgentsQuery,
  useGetAgentQuery,
  useLazyGetAgentTemplateGraphQuery,
  useCreateAgentMutation,
  usePatchAgentMutation,
  useSetAgentStatusMutation,
  useChangeAgentOwnerMutation,
  useAttachPlayMutation,
  useDuplicateAgentMutation,
  useDeleteAgentMutation,
  useRunAgentTestMutation,
  useListAgentRunsQuery,
  useGetAgentCrmScanQuery,
  useListDuplicateGroupsInfiniteQuery,
  useGetDuplicateGroupsSummaryQuery,
  useGetDuplicateGroupQuery,
  usePatchDuplicateGroupMutation,
  useDecideDuplicateGroupsMutation,
  useExportDuplicateGroupsMutation,
  useGetAgentsActivityQuery,
  useGetPlayProposalQuery,
  useStartCandidateTestMutation,
  useGetCandidateTestQuery,
  useAcceptPlayProposalMutation,
  useDiscardPlayProposalMutation,
  useSetPlayMailboxMutation,
  useListSkillsQuery,
  useGetSkillQuery,
  useCreateSkillMutation,
  usePatchSkillMutation,
  useDeleteSkillMutation,
} = gtmAgentsApi;
